import './index.scss';

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useCommunity } from "contexts/community";
import { useAuth } from "@contexts/AuthContext";
import { useChat } from "@contexts/ChatContext";

import { CommunityBanner, UserAvatar } from "@components/Community";

import WelcomeVideo from '../WelcomeVideo';

import { Button, Text } from "@fragments";
import Popper from "@fragments/Popper";

import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import { ArrowDropDownRounded, LinkRounded, ShareRounded } from "@mui/icons-material";
import * as Material from "@mui/material";

import {
    IconBrandInstagram,
    IconBrandTiktok,
    IconBrandWhatsapp,
    IconBrandYoutube,
    IconHelpCircle,
    IconLink,
    IconMail,
    IconMessageCircle,
    IconUsers,
    IconWorld,
} from "@tabler/icons-react";

const LINK_TYPE_ICON = {
    website: <IconWorld size={18} />,
    support: <IconHelpCircle size={18} />,
    whatsapp: <IconBrandWhatsapp size={18} />,
    instagram: <IconBrandInstagram size={18} />,
    tiktok: <IconBrandTiktok size={18} />,
    youtube: <IconBrandYoutube size={18} />,
    email: <IconMail size={18} />,
    default: <IconLink size={18} />,
};

const SOCIAL_TYPES = new Set(['whatsapp', 'instagram', 'tiktok', 'youtube']);

const VALID_LINK_TYPES = new Set(Object.keys(LINK_TYPE_ICON));

function iconForType(type) {
    return LINK_TYPE_ICON[type] || <LinkRounded fontSize="small" />;
}

/**
 * Legacy backend payloads had `title` and `type` swapped on community links.
 * If `type` isn't a valid enum value but `title` is, swap them so this view
 * partitions and labels links correctly.
 */
function healLink(link) {
    if (!link || typeof link !== 'object') return link;
    const typeIsValid = VALID_LINK_TYPES.has(link.type);
    const titleLooksLikeType = VALID_LINK_TYPES.has(link.title);
    if (!typeIsValid && titleLooksLikeType) {
        return { ...link, title: link.type, type: link.title };
    }
    return link;
}

// The computed support-chat URL carries the owner's hashed id — the fallback
// when the payload's admins list is missing.
function userIdFromChatUrl(url) {
    try {
        return new URL(url).searchParams.get('user_id');
    } catch (_) {
        return null;
    }
}

function partitionLinks(links = []) {
    const support = [];
    const social = [];
    const other = [];
    for (const raw of links) {
        const link = healLink(raw);
        const type = link?.type || 'default';
        if (type === 'support') support.push(link);
        else if (SOCIAL_TYPES.has(type)) social.push(link);
        else other.push(link);
    }
    return { support, social, other };
}



export default function Hero({ info, handleOpenUsers, loading }) {
    const { t } = useTranslation('community');

    const { community: communityManagement } = useCommunity();
    const { user: viewer } = useAuth();
    const { openChat, toggle: openInbox } = useChat();

    const [loadingState, setLoadingState] = useState(false);

    const [subscribed, setSubscribed] = useState(info ? ['creator', 'owner', 'member'].includes(info.role) : false);

    // The role can change under this component (e.g. the visitor accepts the
    // invitation and the page reloads the community) — follow it.
    useEffect(() => {
        setSubscribed(info ? ['creator', 'owner', 'member'].includes(info.role) : false);
    }, [info?.role]);

    const moreLinksPopperRef = useRef();

    async function handleSubscribeClick() {
        setLoadingState(true);
        await communityManagement.subscribe(info.id, () => {
            setSubscribed(true);
        });
        setLoadingState(false);
    }

    async function handleUnsubscribeClick() {
        setLoadingState(true);
        await communityManagement.unsubscribe(info.id, () => {
            setSubscribed(false);
        });
        setLoadingState(false);
    }

    function getSafeLink(link) {
        if (link?.includes('http')) return link;
        return 'https://' + link;
    }

    // A chat-mode support link opens the platform conversation with the owner;
    // any other support link stays an external URL.
    function openSupportLink(link) {
        if (link?.mode !== 'chat') {
            window.open(getSafeLink(link.url), '_blank');
            return;
        }

        const owner = (info?.members?.admins ?? []).find((admin) => admin?.role === 'owner');
        const ownerId = owner?.id ?? userIdFromChatUrl(link.url);
        if (!ownerId) return;

        // The owner is the support contact: for them the button is the inbox, not a chat with themselves.
        if (viewer?.id != null && String(viewer.id) === String(ownerId)) {
            openInbox();
            return;
        }

        openChat({ user_id: ownerId, username: owner?.username ?? owner?.nickname, avatar: owner?.avatar });
    }


    return (
        <div className="community-general-info mb-3">
            <div className="description">
                <CommunityBanner className="banner" size={128} banner={info?.banner} />

                <div className="pt-3" style={{ width: '100%' }}>
                    <div className="members-bar-content-inner">
                        <div className="community-title-container">
                            <div className="community-title">
                                <Text.h1 weight={500}>
                                    {info?.name}
                                </Text.h1>
                                {
                                    subscribed && info.role !== 'creator' && <Button
                                        tooltip={t('header.manageSubscription')}
                                        color="inverse"
                                        size="small"
                                        loading={loadingState}
                                        endIcon={<ArrowDropDownRounded />}
                                        onClick={handleUnsubscribeClick}
                                    >
                                        {t('header.subscribed')}
                                    </Button>
                                }
                            </div>

                            {
                                !subscribed && info.role !== 'creator' && <Button
                                    tooltip={t('header.subscribe')}
                                    color="inverse"
                                    size="small"
                                    loading={loadingState}
                                    endIcon={<ArrowDropDownRounded />}
                                    onClick={handleSubscribeClick}
                                >
                                    {t('header.subscribe')}
                                </Button>
                            }

                            <div className="actions-row">
                                {
                                    info.isAdmin() && (
                                        <Button
                                            tooltip={t('header.editCommunity')}
                                            color="overlay"
                                            onClick={() => {
                                                communityManagement.management.open(info);
                                            }}
                                            icon={<SettingsRoundedIcon />}
                                        />
                                    )
                                }
                                {/* The whole payload, not the id: a visitor who
                                    isn't a member yet has no entry in the
                                    context's community list to resolve. */}
                                <Button
                                    tooltip={t('header.shareCommunity')}
                                    onClick={() => {
                                        communityManagement.triggerShareModal(info);
                                    }}
                                    color="overlay"
                                    preventLoading
                                    endIcon={<ShareRounded />}>
                                    {t('common:actions.share')}
                                </Button>
                            </div>
                        </div>
                        <div className="communiy-description-info">
                            <div className="community-avatars">
                                {(info.members?.admins ?? []).slice(0, 3).map((admin, index) => (
                                    <div style={{ marginRight: '7px' }}>
                                        <UserAvatar size={24} user={admin} displayName={true} className="bsf-9 pr-1" style={{ padding: '4px', borderRadius: '50px' }} />
                                    </div>
                                ))}
                            </div>
                            {
                                (info.members?.admins ?? []).length > 3 && (
                                    <span style={{ opacity: 0.3, transform: 'translateY(1px)' }}>
                                        {t('header.moreCreators', { value: (info.members?.admins ?? []).length - 3 })}
                                    </span>
                                )
                            }
                            <span className='csf-5' style={{ transform: 'translateY(1px)' }} >
                                {t('header.members', { value: window.helpers.pretty.int(info?.members?.total ?? 0) })}
                            </span>

                            {info.isAdmin() && <Button
                                color="overlay"
                                variant="text"
                                size="small"
                                startIcon={<IconUsers size={18} />}
                                onClick={handleOpenUsers}
                            >
                                {t('header.manageMembers')}
                            </Button>}
                        </div>

                        <div className="mt-2 mb-2 text csf-2">
                            {
                                info?.description?.split("\n").map((line, key) => (
                                    <span className="description-line csf-4" key={key}>{line}</span>
                                ))
                            }

                            {
                                info?.links?.length > 0 && (() => {
                                    const { support, social, other } = partitionLinks(info.links);

                                    function openMoreLinks(e) {
                                        const options = other.slice(1).map((link, i) => ({
                                            id: `more-link-${i}`,
                                            label: link.title || link.url,
                                            icon: iconForType(link.type),
                                            onClick: () => window.open(getSafeLink(link.url), '_blank'),
                                        }));
                                        moreLinksPopperRef.current?.open(options, 'bottom-start', e.currentTarget);
                                    }

                                    const firstOther = other[0];
                                    const extraCount = other.length - 1;

                                    return (
                                        <div className="description-links mt-2" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                            {support.map((link, key) => (
                                                <Button
                                                    key={`support-${key}`}
                                                    color="inverse"
                                                    size="small"
                                                    startIcon={link.mode === 'chat' ? <IconMessageCircle size={18} /> : iconForType('support')}
                                                    onClick={() => openSupportLink(link)}
                                                >
                                                    {link.title || t('header.supportLink')}
                                                </Button>
                                            ))}

                                            {social.length > 0 && (
                                                <div className="social-links-group" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    {social.map((link, key) => (
                                                        <Button
                                                            key={`social-${key}`}
                                                            color="overlay"
                                                            size="small"
                                                            icon={iconForType(link.type)}
                                                            tooltip={link.title || link.type}
                                                            onClick={() => window.open(getSafeLink(link.url), '_blank')}
                                                        />
                                                    ))}
                                                </div>
                                            )}

                                            {firstOther && (
                                                <Button
                                                    color="overlay"
                                                    size="small"
                                                    startIcon={iconForType(firstOther.type)}
                                                    onClick={() => window.open(getSafeLink(firstOther.url), '_blank')}
                                                >
                                                    {firstOther.title || firstOther.url}
                                                </Button>
                                            )}

                                            {extraCount > 0 && (
                                                <Button
                                                    color="overlay"
                                                    size="small"
                                                    onClick={openMoreLinks}
                                                >
                                                    {t('header.moreLinks', { count: extraCount })}
                                                </Button>
                                            )}

                                            <Popper controlRef={moreLinksPopperRef} />
                                        </div>
                                    );
                                })()
                            }
                        </div>

                        <WelcomeVideo info={info} key={`welcome-${info?.id}`} />
                    </div>
                </div>
            </div>
        </div>
    );
}